// 표 칸 채우기 손잡이(2026-10-20 오너 요청 "엑셀처럼 칸 선택한 후에 모서리 드래그하면 숫자의
// 경우 연속 번호가 드래그 된 영역까지 입력되게").
//
// 엑셀과 같은 규칙:
// - 고른 범위 오른쪽 아래 손잡이를 위·아래·왼쪽·오른쪽 한 방향으로 끌어 늘린다. 두 방향을
//   함께 넘으면 더 많이 넘은 쪽 하나만.
// - 숫자만 있으면 이어지는 번호 — 한 칸이면 1씩, 여러 칸이면 그 간격으로(1,3 → 5,7…).
//   끝에 숫자가 붙은 글자(A1 → A2, 3번 → 4번)와 앞자리 0(01 → 02)도 잇는다.
// - 그 밖의 글자는 원래 순서대로 되풀이한다.
// - **글자만** 채운다. 입력 칸 설정·글자 모양·음영은 그대로 둔다(칸마다 정해 둔 입력 칸을
//   덮어쓰지 않게). 병합에 가려진 칸은 건너뛴다.

import { layoutExpandRange, type LayoutTable } from '@bhops/shared';
import type { CellPos, CellRange } from './useLayoutEditor';

interface Box {
  r1: number;
  r2: number;
  c1: number;
  c2: number;
}

/** 끝에 붙은 숫자 — 앞 글자 + 숫자(소수 포함). "A-1" 의 "-" 는 앞 글자다. */
const NUM_TAIL = /^(.*?)(\d+(?:\.\d+)?)$/;

interface Tail {
  prefix: string;
  num: number;
  decimals: number;
  /** 앞자리 0 이 붙은 정수부 폭(01 → 2). 0 이 없으면 0. */
  pad: number;
}

function parseTail(s: string): Tail | null {
  const m = NUM_TAIL.exec(s.trim());
  if (!m) return null;
  const digits = m[2];
  let prefix = m[1];
  let sign = 1;
  // 숫자 하나뿐인 "-3" 은 음수다.
  if (prefix === '-') {
    prefix = '';
    sign = -1;
  }
  const [int, frac] = digits.split('.');
  return {
    prefix,
    num: sign * Number(digits),
    decimals: frac?.length ?? 0,
    pad: int.length > 1 && int.startsWith('0') ? int.length : 0,
  };
}

function format(prefix: string, n: number, decimals: number, pad: number): string {
  const [int, frac] = Math.abs(n).toFixed(decimals).split('.');
  const body = (pad ? int.padStart(pad, '0') : int) + (frac !== undefined ? `.${frac}` : '');
  return `${prefix}${n < 0 ? '-' : ''}${body}`;
}

/**
 * 원본 글자들(끄는 방향 순서)을 `count` 칸 더 잇는다. `backward` 면 원본 앞쪽으로(위·왼쪽)
 * 늘리는 것이라 돌려주는 값도 원본에서 먼 쪽부터가 아니라 **가까운 쪽부터**다.
 */
export function fillSeries(source: string[], count: number, backward = false): string[] {
  if (count <= 0) return [];
  if (source.length === 0) return Array.from({ length: count }, () => '');
  const parsed = source.map((v) => (v.trim() ? parseTail(v) : null));
  const first = parsed[0];
  if (first && parsed.every((t) => t !== null && t.prefix === first.prefix)) {
    const ts = parsed as Tail[];
    const last = ts[ts.length - 1];
    const step = ts.length === 1 ? 1 : (last.num - first.num) / (ts.length - 1);
    const stepDecimals = Number.isInteger(step) ? 0 : (String(step).split('.')[1]?.length ?? 0);
    const decimals = Math.min(6, Math.max(stepDecimals, ...ts.map((t) => t.decimals)));
    const pad = Math.max(...ts.map((t) => t.pad));
    const out: string[] = [];
    for (let i = 1; i <= count; i++) {
      const n = backward ? first.num - step * i : last.num + step * i;
      out.push(format(first.prefix, n, decimals, pad));
    }
    return out;
  }
  // 되풀이 — 원본 순서 그대로. 앞쪽으로 늘리면 원본 끝에서부터 거꾸로.
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(
      backward
        ? source[(((source.length - 1 - i) % source.length) + source.length) % source.length]
        : source[i % source.length],
    );
  }
  return out;
}

/** 끈 자리(target)가 고른 범위에서 어느 쪽으로 몇 칸 나갔나. 안쪽이면 null. */
export function fillDirection(
  src: Box,
  target: CellPos,
): { dir: 'down' | 'up' | 'right' | 'left'; to: number } | null {
  const down = target.r - src.r2;
  const up = src.r1 - target.r;
  const right = target.c - src.c2;
  const left = src.c1 - target.c;
  const v = Math.max(down, up);
  const h = Math.max(right, left);
  if (v <= 0 && h <= 0) return null;
  if (v >= h) return down > 0 ? { dir: 'down', to: target.r } : { dir: 'up', to: target.r };
  return right > 0 ? { dir: 'right', to: target.c } : { dir: 'left', to: target.c };
}

/** 끄는 동안 보여 줄 범위(원본 + 늘어날 칸). */
export function fillPreviewRange(
  t: LayoutTable,
  range: CellRange,
  target: CellPos,
): CellRange | null {
  const src = layoutExpandRange(t, range.a, range.b);
  const d = fillDirection(src, target);
  if (!d) return null;
  const r1 = d.dir === 'up' ? d.to : src.r1;
  const r2 = d.dir === 'down' ? d.to : src.r2;
  const c1 = d.dir === 'left' ? d.to : src.c1;
  const c2 = d.dir === 'right' ? d.to : src.c2;
  return { a: { r: r1, c: c1 }, b: { r: r2, c: c2 } };
}

/**
 * 채운 표와 채운 뒤 고를 범위. 늘어날 칸이 없으면 null.
 */
export function applyCellFill(
  t: LayoutTable,
  range: CellRange,
  target: CellPos,
): { table: LayoutTable; range: CellRange } | null {
  const src = layoutExpandRange(t, range.a, range.b);
  const d = fillDirection(src, target);
  if (!d) return null;
  const cells = t.cells.map((row) => row.slice());
  const text = (r: number, c: number) => {
    const cell = t.cells[r]?.[c];
    return cell && !cell.covered ? (cell.text ?? '') : null;
  };
  const put = (r: number, c: number, v: string) => {
    const cell = cells[r]?.[c];
    if (!cell || cell.covered) return;
    cells[r][c] = { ...cell, text: v };
  };

  if (d.dir === 'down' || d.dir === 'up') {
    const rows = d.dir === 'down' ? d.to - src.r2 : src.r1 - d.to;
    for (let c = src.c1; c <= src.c2; c++) {
      const source: string[] = [];
      for (let r = src.r1; r <= src.r2; r++) {
        const v = text(r, c);
        if (v !== null) source.push(v);
      }
      const vals = fillSeries(source, rows, d.dir === 'up');
      vals.forEach((v, i) => put(d.dir === 'down' ? src.r2 + 1 + i : src.r1 - 1 - i, c, v));
    }
  } else {
    const cols = d.dir === 'right' ? d.to - src.c2 : src.c1 - d.to;
    for (let r = src.r1; r <= src.r2; r++) {
      const source: string[] = [];
      for (let c = src.c1; c <= src.c2; c++) {
        const v = text(r, c);
        if (v !== null) source.push(v);
      }
      const vals = fillSeries(source, cols, d.dir === 'left');
      vals.forEach((v, i) => put(r, d.dir === 'right' ? src.c2 + 1 + i : src.c1 - 1 - i, v));
    }
  }
  return { table: { ...t, cells }, range: fillPreviewRange(t, range, target)! };
}
